// src/pages/AcceptInvite.jsx
import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { supabase } from '../supabaseClient';
import * as BreweryService from '../services/breweryService';

export default function AcceptInvite() {
  const [state, setState] = useState('loading'); // loading, success, error, needsAuth
  const [message, setMessage] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [token, setToken] = useState('');
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);

  // Listen for auth state changes (lightweight, local to this component)
  useEffect(() => {
    let mounted = true;

    // Check current session first
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) {
        setUser(session?.user || null);
        setAuthReady(true);
        console.log('🔐 AcceptInvite: Initial auth check, user:', session?.user?.email || 'none');
      }
    });

    // Subscribe to auth changes (for when user signs in/up)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (mounted) {
        console.log('🔐 AcceptInvite: Auth state changed:', event, session?.user?.email || 'none');
        setUser(session?.user || null);
        setAuthReady(true);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Process invite once auth is ready
  useEffect(() => {
    if (!authReady) return;

    const acceptInviteToken = async () => {
      // Get token from URL query params
      const params = new URLSearchParams(window.location.search);
      const inviteToken = params.get('token');

      if (!inviteToken) {
        setState('error');
        setMessage('No invite token provided');
        return;
      }

      setToken(inviteToken);

      if (!user) {
        // Fetch invite details using Edge Function (bypasses RLS)
        try {
          const { data: inviteData, error } = await supabase.functions.invoke('get-invite-details', {
            body: { token: inviteToken }
          });

          if (inviteData?.success) {
            setInviteEmail(inviteData.data.email);
            // Store additional invite details in sessionStorage
            sessionStorage.setItem('pendingInviteName', inviteData.data.name || '');
            sessionStorage.setItem('pendingInviteRole', inviteData.data.role || '');
            sessionStorage.setItem('pendingInviteBreweryName', inviteData.data.breweryName || '');
          } else {
            console.log('Could not fetch invite details:', inviteData?.error || error);
          }
        } catch (err) {
          console.log('Could not fetch invite details:', err);
        }

        setState('needsAuth');
        setMessage('Please sign in or create an account to accept this invitation');
        return;
      }

      // User is authenticated, attempt to accept invite
      console.log('🎫 Attempting to accept invite for user:', user.email);
      const result = await BreweryService.acceptInvite(inviteToken);

      if (result.success) {
        setState('success');
        setMessage('Invite accepted! You now have access to the brewery.');
        
        // Set flag to prevent onboarding modal from showing after reload
        sessionStorage.setItem('justAcceptedInvite', 'true');
        
        // Clear any stored invite data
        sessionStorage.removeItem('pendingInviteToken');
        sessionStorage.removeItem('pendingInviteEmail');
        sessionStorage.removeItem('pendingInviteName');
        sessionStorage.removeItem('pendingInviteRole');
        sessionStorage.removeItem('pendingInviteBreweryName');
        
        // Reload page to refresh brewery data and show the brewery dashboard
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        // Check if invite was already accepted
        if (result.error && result.error.includes('already accepted')) {
          setState('success');
          setMessage('You already have access to this brewery!');
          
          // Clear any stored invite data
          sessionStorage.removeItem('pendingInviteToken');
          sessionStorage.removeItem('pendingInviteEmail');
          sessionStorage.removeItem('pendingInviteName');
          sessionStorage.removeItem('pendingInviteRole');
          sessionStorage.removeItem('pendingInviteBreweryName');
          
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
        } else {
          setState('error');
          setMessage(result.error || 'Failed to accept invite');
        }
      }
    };

    acceptInviteToken();
  }, [authReady, user]);


  const goToLogin = () => {
    // Store the invite token and email in sessionStorage so Login.jsx can detect invite context
    if (token) {
      sessionStorage.setItem('pendingInviteToken', token);
    }
    if (inviteEmail) {
      sessionStorage.setItem('pendingInviteEmail', inviteEmail);
    }
    window.location.href = '/?login=true&invite=1';
  };

  const goToDashboard = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        {/* Loading */}
        {state === 'loading' && (
          <div className="text-center">
            <Loader className="w-12 h-12 text-amber-600 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Processing Invite
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we process your invitation...
            </p>
          </div>
        )}

        {/* Success */}
        {state === 'success' && (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Welcome!
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {message}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Redirecting to dashboard in 3 seconds...
            </p>
          </div>
        )}

        {/* Needs Authentication */}
        {state === 'needsAuth' && (
          <div className="text-center">
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🔐</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Sign In Required
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              {message}
            </p>
            {inviteEmail && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                This invitation was sent to: <strong>{inviteEmail}</strong>
              </p>
            )}
            <button
              onClick={goToLogin}
              className="block w-full px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition font-medium"
            >
              Sign In / Create Account
            </button>
          </div>
        )}

        {/* Error */}
        {state === 'error' && (
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Oops!
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {message}
            </p>
            <div className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Make sure you're logged in with the email the invite was sent to.
              </p>
              <button
                onClick={goToLogin}
                className="block w-full px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition font-medium"
              >
                Go to Login
              </button>
              <button
                onClick={goToDashboard}
                className="block w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition font-medium"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
